export type ConditionExpression =
  | { op: 'AND'; args: ConditionExpression[] }
  | { op: 'OR'; args: ConditionExpression[] }
  | { op: 'NOT'; arg: ConditionExpression }
  | { op: 'exists'; fieldCode: string }
  | { op: 'empty'; fieldCode: string }
  | {
      op: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains';
      fieldCode: string;
      value?: unknown;
    };

function evaluateComparison(
  condition: Extract<
    ConditionExpression,
    { op: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' }
  >,
  payload: Record<string, unknown>,
): boolean {
  const actual = payload[condition.fieldCode];
  const { value } = condition;
  switch (condition.op) {
    case 'eq':
      return actual === value;
    case 'neq':
      return actual !== value;
    case 'gt':
      return Number(actual) > Number(value);
    case 'lt':
      return Number(actual) < Number(value);
    case 'gte':
      return Number(actual) >= Number(value);
    case 'lte':
      return Number(actual) <= Number(value);
    case 'contains':
      if (Array.isArray(actual)) return actual.includes(value);
      return typeof actual === 'string' && typeof value === 'string' && actual.includes(value);
  }
}

/** Evaluates a decision/rule condition AST against a flat fieldCode → value payload. */
export function evaluateCondition(
  condition: ConditionExpression,
  payload: Record<string, unknown>,
): boolean {
  switch (condition.op) {
    case 'AND':
      return condition.args.every((arg) => evaluateCondition(arg, payload));
    case 'OR':
      return condition.args.some((arg) => evaluateCondition(arg, payload));
    case 'NOT':
      return !evaluateCondition(condition.arg, payload);
    case 'exists': {
      const value = payload[condition.fieldCode];
      return value !== undefined && value !== null;
    }
    case 'empty': {
      const value = payload[condition.fieldCode];
      if (value === undefined || value === null || value === '') return true;
      return Array.isArray(value) && value.length === 0;
    }
    default:
      return evaluateComparison(condition, payload);
  }
}
